import { useEffect, useRef, useMemo } from 'react';
import history from '../../../routes/history';

/**
 * Hook para detectar cuando se sale de rutas específicas y ejecutar un callback
 * Soporta callbacks síncronos y asíncronos (promesas)
 * Intercepta history.push y history.replace para esperar promesas antes de navegar
 *
 * @param {Object} options - Opciones del hook
 * @param {Function} options.onExit - Callback que se ejecuta al salir de las rutas especificadas. Puede retornar una promesa
 * @param {Array<string>} [options.routes] - Rutas específicas a monitorear. Si no se proporciona, detecta rutas de Kambi (#/)
 * @param {Function} [options.shouldTrigger] - Función personalizada para determinar si debe ejecutarse onExit
 */
export const useExitRoute = ({ onExit, routes, shouldTrigger }) => {
  const previousPathRef = useRef(null);
  const previousHashRef = useRef(null);
  const originalPushRef = useRef(null);
  const originalReplaceRef = useRef(null);
  const isNavigatingRef = useRef(false);
  const memoizedOnExit = useMemo(() => onExit, [onExit]);
  const memoizedShouldTrigger = useMemo(() => shouldTrigger, [shouldTrigger]);

  useEffect(() => {
    // Inicializar la ruta actual
    const initialPath = history.location.pathname;
    const initialHash = window.location.hash || '';
    previousPathRef.current = initialPath;
    previousHashRef.current = initialHash;

    // Guardar los métodos originales
    originalPushRef.current = history.push;
    originalReplaceRef.current = history.replace;

    // Función para determinar si debe ejecutarse el callback
    const shouldExecuteExit = (previousPath, currentPath, previousHash, currentHash) => {
      // Si hay una función personalizada, usarla
      if (memoizedShouldTrigger) {
        return memoizedShouldTrigger({
          previousPath,
          currentPath,
          previousHash,
          currentHash,
        });
      }

      // Si hay rutas específicas, verificar si se salió de alguna de ellas
      if (routes && routes.length > 0) {
        const wasInRoute = routes.some(
          route => previousPath === route || previousPath.startsWith(route)
        );
        return wasInRoute && previousPath !== currentPath;
      }

      // Por defecto, detectar navegación interna de Kambi (patrón #/)
      const previousWasKambiRoute = previousHash && previousHash.startsWith('#/');
      const currentIsKambiRoute = currentHash.startsWith('#/');
      const isKambiInternalNavigation = previousWasKambiRoute && currentIsKambiRoute;

      // Solo ejecutar si NO es navegación interna de Kambi
      return !isKambiInternalNavigation && previousPath !== currentPath;
    };

    // Función auxiliar para verificar si un valor es una promesa
    const isPromise = value => {
      return value && typeof value.then === 'function' && typeof value.catch === 'function';
    };

    // Función para ejecutar onExit y esperar si es necesario
    const executeOnExit = async (currentPath, nextPath, currentHash, nextHash) => {
      if (isNavigatingRef.current) {
        // Ya hay una navegación en progreso, no ejecutar de nuevo
        return;
      }

      if (shouldExecuteExit(currentPath, nextPath, currentHash, nextHash)) {
        console.log('Ejecutando onExit antes de navegar...', {
          currentPath,
          nextPath,
          currentHash: currentHash || 'none',
          nextHash: nextHash || 'none',
        });

        isNavigatingRef.current = true;
        try {
          const result = memoizedOnExit();

          // Si retorna una promesa, esperar a que se complete
          if (isPromise(result)) {
            await result;
          }
        } catch (error) {
          console.error('Error en onExit:', error);
        } finally {
          isNavigatingRef.current = false;
        }
      }
    };

    // Interceptar history.push
    history.push = async function(path, state) {
      const currentPath = history.location.pathname;
      const currentHash = window.location.hash || '';

      // Determinar el siguiente path
      let nextPath = path;
      let nextHash = currentHash;

      if (typeof path === 'string') {
        const [pathname, hash] = path.split('#');
        nextPath = pathname;
        nextHash = hash ? `#${hash}` : '';
      } else if (path && path.pathname) {
        nextPath = path.pathname;
        nextHash = path.hash || currentHash;
      }

      // Ejecutar onExit y esperar si es necesario
      await executeOnExit(currentPath, nextPath, currentHash, nextHash);

      // Actualizar referencias
      previousPathRef.current = nextPath;
      previousHashRef.current = nextHash;

      // Llamar al método original
      return originalPushRef.current.call(history, path, state);
    };

    // Interceptar history.replace
    history.replace = async function(path, state) {
      const currentPath = history.location.pathname;
      const currentHash = window.location.hash || '';

      // Determinar el siguiente path
      let nextPath = path;
      let nextHash = currentHash;

      if (typeof path === 'string') {
        const [pathname, hash] = path.split('#');
        nextPath = pathname;
        nextHash = hash ? `#${hash}` : '';
      } else if (path && path.pathname) {
        nextPath = path.pathname;
        nextHash = path.hash || currentHash;
      }

      // Ejecutar onExit y esperar si es necesario
      await executeOnExit(currentPath, nextPath, currentHash, nextHash);

      // Actualizar referencias
      previousPathRef.current = nextPath;
      previousHashRef.current = nextHash;

      // Llamar al método original
      return originalReplaceRef.current.call(history, path, state);
    };

    // Listener de cambios de ruta para actualizar referencias (backup y para otros métodos de navegación)
    const unlisten = history.listen(location => {
      const currentPath = location.pathname;
      const currentHash = window.location.hash || '';

      // Solo actualizar si realmente cambió (para evitar actualizaciones innecesarias)
      if (previousPathRef.current !== currentPath || previousHashRef.current !== currentHash) {
        previousPathRef.current = currentPath;
        previousHashRef.current = currentHash;
      }
    });

    // Cleanup: restaurar los métodos originales y remover el listener
    return () => {
      if (originalPushRef.current) {
        history.push = originalPushRef.current;
      }
      if (originalReplaceRef.current) {
        history.replace = originalReplaceRef.current;
      }
      unlisten();
    };
  }, [memoizedOnExit, routes, memoizedShouldTrigger]);
};

export default useExitRoute;
